<script lang="ts" setup>
const promoCode = ref('');
const promoInputError = ref<string>('');
const promoInputHint = ref<string>('');
const isLoading = ref(false);
const isPromoCodeApplied = ref(false);

const cartStore = useCartStore();
const { cart, selectedCartItems } = storeToRefs(cartStore);

const isError = computed(() => {
  return !!(cart.value.promo_errors?.length || cart.value.promo_warnings?.length === selectedCartItems.value.length);
});

onMounted(() => {
  if (cart.value.calculate.promo_code?.code) {
    promoCode.value = cart.value.calculate.promo_code.code;
    isPromoCodeApplied.value = true;
  }
});

watch(
  () => cart.value.calculate.promo_code?.code,
  () => {
    if (cart.value.calculate.promo_code?.code) {
      promoCode.value = cart.value.calculate.promo_code.code;
      isPromoCodeApplied.value = true;
    } else {
      promoCode.value = '';
      isPromoCodeApplied.value = false;
    }
  },
  { immediate: true },
);

watch(
  () => promoCode.value,
  () => {
    if (promoCode.value && promoCode.value.length) {
      if (promoCode.value === cart.value.calculate.promo_code?.code) {
        isPromoCodeApplied.value = true;
      } else {
        isPromoCodeApplied.value = false;
        promoInputHint.value = 'Промокод применяется только к товарам без скидки';
      }
    } else {
      promoInputHint.value = '';
    }
    promoInputError.value = '';
  },
);

const { sendPromoCodeEventToDataLayer } = useGtmModule();

async function applyPromoCode() {
  isLoading.value = true;

  await sendPromoCodeEventToDataLayer('promocode_apply', promoCode.value);
  await cartStore.applyPromoCode(promoCode.value);

  if (cartStore.cart.promo_errors) {
    promoInputError.value = cartStore.cart.promo_errors.join('<br/>');
  } else if (cart.value.calculate.promo_code?.code) {
    await sendPromoCodeEventToDataLayer('promocode_applied', promoCode.value);
    promoInputHint.value = '';
    isPromoCodeApplied.value = true;
  }

  isLoading.value = false;
}

async function removePromoCode() {
  isLoading.value = true;
  await cartStore.removePromoCode();

  isPromoCodeApplied.value = false;
  promoCode.value = '';

  isLoading.value = false;
}

function handleClearAndClose() {
  if (cart.value.calculate.promo_code?.code) {
    promoCode.value = cart.value.calculate.promo_code?.code;
  } else {
    isPromoCodeApplied.value = false;
  }
}
</script>

<template>
  <div class="promo-code-input-block">
    <InputField
      v-model="promoCode"
      :append-button-icon="isPromoCodeApplied && !promoInputError && !isError ? 'Check' : 'ChevronRight'"
      :error="promoInputError"
      :hint="promoInputHint"
      :is-loading="isLoading"
      :is-show-clear-btn="promoCode.length > 0 && !!promoInputError"
      :is-success="isPromoCodeApplied"
      name="promo-code"
      placeholder="Промокод или сертификат"
      @close="handleClearAndClose"
      @submit="applyPromoCode"
    />

    <div
      v-if="cart.promo_warnings?.length && selectedCartItems.length"
      class="promo-code-input-block__applied-code"
    >
      <template v-if="cart.promo_warnings.length === selectedCartItems.length">
        Примененный промокод

        <button
          class="promo-code-input-block__remove-code-btn"
          @click="removePromoCode"
        >
          <span class="text-hell-black">{{ cart.calculate.promo_code?.code }}</span>

          <SvgoCross filled />
        </button>

        не&nbsp;действует на выбранные товары в текущей корзине.
      </template>

      <template v-else>
        Промокод

        <button
          class="promo-code-input-block__remove-code-btn"
          @click="removePromoCode"
        >
          <span class="text-hell-black">{{ cart.calculate.promo_code?.code }}</span>

          <SvgoCross filled />
        </button>

        применен к определенным товарам.
      </template>
    </div>

    <div
      v-else-if="cart.calculate.promo_code?.code"
      class="promo-code-input-block__applied-code"
    >
      Применен промокод:

      <button
        class="promo-code-input-block__remove-code-btn"
        @click="removePromoCode"
      >
        <span class="text-hell-black">{{ cart.calculate.promo_code?.code }}</span>

        <SvgoCross filled />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.promo-code-input-block {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__applied-code {
    display: inline;
    font-size: $text-xs;
  }

  &__remove-code-btn {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 6px 8px;
    border: 1px solid $hell-black-10;
    border-radius: 8px;
    font-size: $text-sm;
    transition: all 0.3s;

    &:hover {
      border-color: $hell-red;
      color: $hell-red;
    }
  }
}
</style>
